/**
 * source-panels.tsx — Nguồn AI và lượt AI: thẻ "Nhúng tài liệu (Kho tri thức)". (Ô chọn nguồn riêng từng nền tảng đã bỏ —
 * nguồn là MỘT lựa chọn cho cả tổ chức, xem sources/source-mode-card.tsx.)
 */
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { BookOpen } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useUpdateCreditSettings } from '@/hooks/use-ai-credits'
import {
  aiErrorMessage, embeddingProgress, formatCredits, type AiCredits, type EmbeddingOverage,
} from '@/lib/ai-credits'
import { ToneBadge } from './credit-widgets'

/** Hai lựa chọn dạng thẻ (lựa chọn khi vượt trần nhúng). */
function OptionPair<T extends string>({
  value, options, onPick, disabled, ariaLabel,
}: {
  value: T
  options: { id: T; title: string; desc: string }[]
  onPick: (v: T) => void
  disabled?: boolean
  ariaLabel: string
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          disabled={disabled}
          onClick={() => { if (value !== o.id) onPick(o.id) }}
          className={cn(
            'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            value === o.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50',
          )}
        >
          <span className="block text-sm font-semibold">{o.title}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">{o.desc}</span>
        </button>
      ))}
    </div>
  )
}

/** Thẻ "Nhúng tài liệu (Kho tri thức)": đã dùng / trần hôm nay + lựa chọn khi vượt trần. */
export function EmbeddingCard({ credits, canEdit, planName }: { credits: AiCredits; canEdit: boolean; planName?: string | null }) {
  const { t } = useTranslation('aiOps')
  const update = useUpdateCreditSettings()
  const emb = credits.embedding
  if (!emb) return null
  const { percent, capped } = embeddingProgress(emb)
  const pick = (v: EmbeddingOverage) =>
    update.mutate(
      { embeddingOverage: v },
      {
        onSuccess: () => toast.success(t('sourcePanels.overageSaved')),
        onError: (err) => toast.error(aiErrorMessage(err)),
      },
    )
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> {t('sourcePanels.title')}</CardTitle>
        <CardDescription><Trans t={t} i18nKey="sourcePanels.desc" components={{ b: <b /> }} /></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span>
              <Trans
                t={t} i18nKey="sourcePanels.usedToday"
                values={{ used: formatCredits(emb.usedToday), cap: formatCredits(emb.freeCap) }}
                components={{ b: <b className="tabular-nums" /> }}
              />
              {planName ? t('sourcePanels.planSuffix', { name: planName }) : ''}
            </span>
            {capped ? <ToneBadge tone="warning">{t('sourcePanels.capped')}</ToneBadge> : <ToneBadge tone="success">{t('sourcePanels.withinFree')}</ToneBadge>}
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
            <div className={cn('h-full rounded-full', capped ? 'bg-warning' : 'bg-primary')} style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>{t('sourcePanels.overageLabel')}</Label>
          <OptionPair<EmbeddingOverage>
            ariaLabel={t('sourcePanels.overageAria')}
            value={emb.overage}
            disabled={!canEdit || update.isPending}
            onPick={pick}
            options={[
              { id: 'credit', title: t('sourcePanels.creditTitle'), desc: t('sourcePanels.creditDesc') },
              { id: 'stop', title: t('sourcePanels.stopTitle'), desc: t('sourcePanels.stopDesc') },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )
}
